import z from "zod";
import { isValidLatitudeDegree, isValidLongitudeDegree } from "./validation.ts";

export const GeocoordinateLatitudeValueSchema = z.number().lte(90).gte(-90);
export const GeocoordinateLongitudeValueSchema = z.number().lte(180).gte(-180);

export const GeocoordinateSimpleValueSchema = z.tuple([
  GeocoordinateLatitudeValueSchema.nullable(),
  GeocoordinateLongitudeValueSchema.nullable(),
]);
export type GeocoordinateSimpleValueType = z.infer<
  typeof GeocoordinateSimpleValueSchema
>;

export const GeocoordinateSimpleValueGISSchema = z.tuple([
  GeocoordinateLongitudeValueSchema,
  GeocoordinateLatitudeValueSchema,
]);
/**
 * Type representing a geocoordinate tuple following the GIS standard.
 * Uses longitude/latitude order as per GIS conventions.
 */
export type GeocoordinateSimpleValueGISType = z.infer<
  typeof GeocoordinateSimpleValueGISSchema
>;

export const parseCoordinateAsDegree = (value: string): number => {
  const num = Number(value);

  return isNaN(num) ? Number(value.replace(/[^0-9.-/e]/g, "")) : num;
};

export const parseAsLatitudeDegree = (value?: string) => {
  if (!value || (typeof value === "string" && value.replace(/\s/g, "") === ""))
    return null;

  const valueAsNumber = parseCoordinateAsDegree(value);
  const isNorth =
    value.toLowerCase().includes("n") ||
    (!value.toLowerCase().includes("s") && valueAsNumber > 0);

  if (valueAsNumber < 0) return valueAsNumber;

  return isValidLatitudeDegree(valueAsNumber)
    ? isNorth
      ? valueAsNumber
      : -valueAsNumber
    : null;
};

export const parseAsLongitudeDegree = (value?: string) => {
  if (!value || (typeof value === "string" && value.replace(/\s/g, "") === ""))
    return null;

  const valueAsNumber = parseCoordinateAsDegree(value);
  const isEast =
    value.toLowerCase().includes("e") ||
    (!value.toLowerCase().includes("w") && valueAsNumber > 0);

  if (valueAsNumber < 0) return valueAsNumber;

  return isValidLongitudeDegree(valueAsNumber)
    ? isEast
      ? valueAsNumber
      : -valueAsNumber
    : null;
};
